import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { hapticNotification } from "../lib/tg";

type Kind = "success" | "error" | "info";
interface Toast { id: number; kind: Kind; text: string }

interface ToastApi {
  success: (text: string) => void;
  error: (text: string) => void;
  info: (text: string) => void;
}

const Ctx = createContext<ToastApi | null>(null);

let nextId = 1;

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((kind: Kind, text: string) => {
    const id = nextId++;
    setToasts((t) => [...t, { id, kind, text }]);
    if (kind === "error") hapticNotification("error");
    else if (kind === "success") hapticNotification("success");
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4000);
  }, []);

  const api: ToastApi = {
    success: (text) => push("success", text),
    error: (text) => push("error", text),
    info: (text) => push("info", text),
  };

  return (
    <Ctx.Provider value={api}>
      {children}
      <div className="fixed bottom-4 left-3 right-3 z-50 flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`pointer-events-auto rounded-2xl px-4 py-3 shadow-lg text-sm font-medium animate-in fade-in slide-in-from-bottom-2 ${
              t.kind === "success" ? "bg-green-600 text-white" :
              t.kind === "error" ? "bg-red-600 text-white" :
              "bg-tg-secondary text-tg-text"
            }`}
          >{t.text}</div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Fallback when not wrapped (shouldn't happen in production)
    return {
      success: console.log,
      error: (t) => { console.error(t); window.alert(t); },
      info: console.log,
    };
  }
  return ctx;
}

/** Wraps a thenable; toasts the error message if it rejects. */
export function useTry() {
  const toast = useToast();
  return useCallback(
    async <T,>(p: Promise<T> | (() => Promise<T>), opts?: { success?: string; errorPrefix?: string }) => {
      try {
        const result = await (typeof p === "function" ? p() : p);
        if (opts?.success) toast.success(opts.success);
        return result;
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        toast.error(opts?.errorPrefix ? `${opts.errorPrefix}: ${msg}` : msg);
        throw e;
      }
    },
    [toast],
  );
}

// React 19's useEffect cleanup for fading — keep import alive
void useEffect;
